import './App.css';
import {
  init,
  getCurrentUser,
  get,
  set,
  subscribe,
  onAuthStateChanged,
  login
} from '@tarobase/js-sdk';
import { useEffect, useState, memo } from 'react';
import {
  ChakraProvider,
  extendTheme,
  Box,
  Heading,
  Text,
  Input,
  Textarea,
  Button,
  Stack,
  Flex,
  Container,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useParams,
} from 'react-router-dom';
import CodePopover from './components/CodePopover';

const API_URL = 'http://localhost:3002';
//const API_URL = 'https://api.tarobase.com';

// Custom theme with dark purple as the primary color
const theme = extendTheme({
  colors: {
    brand: {
      50: '#EDE9FE',
      100: '#DCD6FA',
      200: '#C0B6F2',
      300: '#A494EA',
      400: '#8873E2',
      500: '#6C52DA',
      600: '#5D3FD3', // Your primary dark purple color
      700: '#4B32A6',
      800: '#3A2679',
      900: '#2A1A4C',
    },
  },
});

function App() {
  return (
    <ChakraProvider theme={theme}>
      <Router>
        <Routes>
          <Route path="/" element={<AppPrompt />} />
          <Route path="/:appId" element={<AppMain />} />
        </Routes>
      </Router>
    </ChakraProvider>
  );
}

function AppPrompt() {
  const [inputAppId, setInputAppId] = useState('6775903b8cc5cf30046a9de2');

  const handleSubmit = () => {
    if (inputAppId) {
      window.location.href = `/${inputAppId}`;
    }
  };

  return (
    <Box bg="white" minH="100vh" py={6}>
      <Container maxW="container.lg">
        <Stack spacing={6}>
          <Heading as="h1" size="lg" color="brand.600">
            Tarobase Sandbox
          </Heading>
          <Text>Please enter your App ID:</Text>
          <Input
            placeholder="App ID"
            value={inputAppId}
            onChange={(e) => setInputAppId(e.target.value)}
          />
          <Button colorScheme="brand" onClick={handleSubmit} w={200}>
            Submit
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}

function AppMain() {
  const { appId } = useParams();
  const [key, setKey] = useState('');
  const [value, setValue] = useState('{}');
  const [path, setPath] = useState('');
  const [getData, setGetData] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [lastTransactionHash, setLastTransactionHash] = useState(null);

  useEffect(() => {
    init({
      appId,
      apiUrl: API_URL,
      authMethod: 'onboard',
    });
  }, [appId]);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    onAuthStateChanged((user) => {
      setUser(user);
      setLoading(false);
    });
  }, []);

  const handleSet = async () => {
    try {
      const parsedValue = JSON.parse(value);
      const setResult = await set(key, parsedValue);
      if (setResult.transactionId) {
        setLastTransactionHash(setResult.transactionId);
      }
    } catch (error) {
      console.error('Invalid JSON:', error);
    }
  };

  const handleGet = async () => {
    try {
      const result = await get(path);
      setGetData(JSON.stringify(result, null, 2));
    } catch (error) {
      setGetData('Error fetching data');
    }
  };

  const handleSubscribe = async () => {
    try {
      const currentPath = path;

      const newSubscription = {
        path: currentPath,
        unsubscribe: null,
        data: 'Subscribed. Waiting for data...',
      };

      setSubscriptions((prevSubs) => [...prevSubs, newSubscription]);

      subscribe(currentPath, {
        onData: (data) => {
          setSubscriptions((prevSubs) => {
            const index = prevSubs.findIndex((sub) => sub.path === currentPath);
            if (index !== -1) {
              const newSubs = [...prevSubs];
              newSubs[index] = {
                ...newSubs[index],
                data: JSON.stringify(data, null, 2),
              };
              return newSubs;
            } else {
              console.error('Subscription not found for path:', currentPath);
              return prevSubs;
            }
          });
        },
        onError: (error) => {
          console.error('Subscription error:', error);
        },
      });

      setSubscriptions((prevSubs) => {
        const index = prevSubs.findIndex((sub) => sub.path === currentPath);
        if (index !== -1) {
          const newSubs = [...prevSubs];
          newSubs[index] = { ...newSubs[index], unsubscribe };
          return newSubs;
        } else {
          console.error('Subscription not found for path:', currentPath);
          return prevSubs;
        }
      });
    } catch (error) {
      console.error('Subscription error:', error);
    }
  };

  const handleUnsubscribe = (unsubscribePath) => {
    const sub = subscriptions.find((sub) => sub.path === unsubscribePath);
    if (sub) {
      sub.unsubscribe();
      setSubscriptions((prevSubs) =>
        prevSubs.filter((sub) => sub.path !== unsubscribePath)
      );
    }
  };

  const handleGetCurrentUser = async () => {
    try {
      const user = await getCurrentUser();
      setCurrentUserData(JSON.stringify(user, null, 2));
    } catch (error) {
      setCurrentUserData('Error fetching user data');
    }
  };

  if (loading) {
    return (
      <Box bg="white" minH="100vh" py={6}>
        <Container maxW="container.lg">
          <Stack spacing={6} align="center">
            <Heading as="h1" size="lg" color="brand.600">
              Tarobase Sandbox
            </Heading>
            <Text>Loading...</Text>
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg="white" minH="100vh" py={6}>
      <Container maxW="container.lg">
        <Stack spacing={6}>
          <Heading as="h1" size="lg" color="brand.600">
            Tarobase Sandbox
          </Heading>

          {/* App ID Displayed for All Users */}
          <Flex alignItems="center">
            <Text fontSize="md" mr={2}>
              App ID: {appId}
            </Text>
            <CodePopover
              code={`init({
  appId: '${appId}',
});`}
            />
          </Flex>

          {user ? (
            <Stack spacing={4}>
              {/* Auth Status */}
              <Flex alignItems="center">
                <Text fontSize="md" mr={2}>
                  Logged in as: {user.address}
                </Text>
                <CodePopover
                  code={`const { login, loading, user } = useAuth();`}
                />
              </Flex>

              {/* Set Key-Value Pair */}
              <Box>
                <Text fontWeight="bold" mb={2}>
                  Set Key-Value Pair
                </Text>
                <Grid templateColumns="1fr auto" gap={2} alignItems="center">
                  <GridItem>
                    <Input
                      placeholder="Enter key"
                      value={key}
                      onChange={(e) => setKey(e.target.value)}
                      mb={2}
                      size="sm"
                    />
                    <Textarea
                      placeholder="Enter JSON value"
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      mb={2}
                      size="sm"
                    />
                  </GridItem>
                  <GridItem>
                    <Flex alignItems="center">
                      <Button
                        colorScheme="brand"
                        size="sm"
                        onClick={handleSet}
                      >
                        Set
                      </Button>
                      <CodePopover code={`await set('${key}', ${value});`} />
                    </Flex>
                  </GridItem>
                </Grid>
                {lastTransactionHash && (
                  <Box mt={2}>
                    <Text fontSize="sm" color="gray.600">
                      Last Transaction Hash: {lastTransactionHash}
                    </Text>
                  </Box>
                )}
              </Box>

              {/* Get Data */}
              <Box>
                <Text fontWeight="bold" mb={2}>
                  Get Data
                </Text>
                <Grid templateColumns="1fr auto" gap={2} alignItems="center">
                  <GridItem>
                    <Input
                      placeholder="Enter path"
                      value={path}
                      onChange={(e) => setPath(e.target.value)}
                      mb={2}
                      size="sm"
                    />
                  </GridItem>
                  <GridItem>
                    <Flex alignItems="center">
                      <Button
                        colorScheme="brand"
                        size="sm"
                        onClick={handleGet}
                      >
                        Get
                      </Button>
                      <CodePopover
                        code={`const data = await get('${path}');`}
                      />
                    </Flex>
                  </GridItem>
                </Grid>
                {getData && (
                  <Box bg="gray.100" p={2} rounded="md" mt={2}>
                    <pre style={{ fontSize: '0.85rem' }}>{getData}</pre>
                  </Box>
                )}
              </Box>

              {/* Subscribe to Path */}
              <Box>
                <Text fontWeight="bold" mb={2}>
                  Subscribe to Path
                </Text>
                <Grid templateColumns="1fr auto" gap={2} alignItems="center">
                  <GridItem>
                    <Input
                      placeholder="Enter path"
                      value={path}
                      onChange={(e) => setPath(e.target.value)}
                      mb={2}
                      size="sm"
                    />
                  </GridItem>
                  <GridItem>
                    <Flex alignItems="center">
                      <Button
                        colorScheme="brand"
                        size="sm"
                        onClick={handleSubscribe}
                      >
                        Subscribe
                      </Button>
                      <CodePopover
                        code={`subscribe('${path}', {
  onData: (data) => {
    // Handle data update
  },
  onError: (error) => {
    console.error('Subscription error:', error);
  },
});`}
                      />
                    </Flex>
                  </GridItem>
                </Grid>
              </Box>

              {/* Active Subscriptions */}
              {subscriptions.length > 0 && (
                <Box>
                  <Heading as="h3" size="sm" mb={2}>
                    Active Subscriptions
                  </Heading>
                  {subscriptions.map((sub) => (
                    <Box
                      key={sub.path}
                      borderWidth="1px"
                      borderRadius="md"
                      p={2}
                      mb={2}
                    >
                      <Flex
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Text fontSize="sm">
                          <strong>Path:</strong> {sub.path}
                        </Text>
                        <Button
                          size="xs"
                          colorScheme="red"
                          onClick={() => handleUnsubscribe(sub.path)}
                        >
                          Unsubscribe
                        </Button>
                      </Flex>
                      <Box bg="gray.50" p={2} rounded="md" mt={2}>
                        <pre style={{ fontSize: '0.75rem' }}>{sub.data}</pre>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Get Current User at the bottom */}
              <Box>
                <Flex alignItems="center">
                  <Button
                    colorScheme="brand"
                    size="sm"
                    onClick={handleGetCurrentUser}
                  >
                    Get Current User
                  </Button>
                  <CodePopover
                    code={`const user = await getCurrentUser();`}
                  />
                </Flex>
                {currentUserData && (
                  <Box bg="gray.100" p={2} rounded="md" mt={2}>
                    <pre style={{ fontSize: '0.85rem' }}>
                      {currentUserData}
                    </pre>
                  </Box>
                )}
              </Box>
            </Stack>
          ) : (
            <Stack spacing={4}>
              {/* Auth Status */}
              <Flex alignItems="center">
                <Text fontSize="md" mr={2}>
                  Not logged in
                </Text>
                <CodePopover
                  code={`const { login, loading, user } = useAuth();`}
                />
              </Flex>

              {/* Login Button */}
              <Flex alignItems="center">
                <Button colorScheme="brand" size="sm" onClick={login}>
                  LOGIN
                </Button>
                <CodePopover code={`login();`} />
              </Flex>
            </Stack>
          )}
        </Stack>
      </Container>
    </Box>
  );
}

export default App;

